package no.vareflow.app.core

import no.vareflow.app.data.json.Json
import no.vareflow.app.data.local.KeyValueStore
import no.vareflow.app.data.local.LocalCodec
import no.vareflow.app.data.local.StorageKeys
import no.vareflow.app.data.local.readCollection
import no.vareflow.app.domain.*
import no.vareflow.app.sync.CategoryDoc
import no.vareflow.app.sync.CloudMapping
import no.vareflow.app.sync.SvinnMapping
import no.vareflow.app.sync.PendingCountWrite
import no.vareflow.app.sync.RemoteCountEntry

/**
 * Platform-neutral app state and actions (users, catalog, monthly sessions, counting, audit),
 * stored through a [KeyValueStore] in the same format as the phone. Used by the web version;
 * ported from the Android VareflowStore (local mode) with the same rules and audit events.
 * In cloud mode ([cloud] set) the data comes from Firestore through the applyCloud… functions and
 * every local change is handed to a [CloudSink] (see "cloud" below).
 *
 * Screens read the public lists and call the actions; every change notifies [addListener]
 * listeners so the UI can redraw.
 */
class VareflowCore(
    private val storage: KeyValueStore,
    private val appVersion: String,
    private val auditSource: String = "web",
    seedProductsJson: () -> String? = { null },
    /** Firebase company/location this core mirrors; null = local test mode with demo data. */
    val cloud: CloudContext? = null
) {
    val users = mutableListOf<UserAccount>()
    val products = mutableListOf<Product>()
    val sessions = mutableListOf<CountSession>()
    val inventorySessions = mutableListOf<InventorySession>()
    val categoryAssignments = mutableListOf<CategoryAssignment>()
    val departments = mutableListOf<InventoryDepartment>()
    private val selectedDepartmentBySession = mutableMapOf<String, String>()
    val countEntries = mutableListOf<InventoryCountEntry>()
    val categories = mutableListOf<String>()
    val spotChecks = mutableListOf<SpotCheckRecord>()
    val auditEvents = mutableListOf<AuditEvent>()
    /** Registered svinn (lost stock), newest first. */
    val svinn = mutableListOf<SvinnRecord>()
    private val categoryOrder = mutableListOf<String>()
    private val productOrderByCategory = mutableMapOf<String, List<String>>()
    private val deletedProductIds = mutableSetOf<String>()
    private val deletedCategoryNames = mutableSetOf<String>()
    var currentInventorySessionId: String? = null
        private set

    /** Problems found while reading local data at startup; shown on the login screen. */
    val loadWarnings = mutableListOf<String>()
    private var usersDataCorrupt = false

    val deviceId: String = storage.getString(KEY_DEVICE_ID) ?: randomId().also { storage.putStrings(mapOf(KEY_DEVICE_ID to it)) }

    private val listeners = mutableListOf<() -> Unit>()
    fun addListener(listener: () -> Unit) { listeners += listener }
    private fun changed() = listeners.toList().forEach { it() }

    init {
        load()
        migratePlaintextPasswords()
        applyPersistedDeletions()
        if (cloud == null) {
            importSeedProductsIfNeeded(seedProductsJson)
            if (categories.isEmpty()) seedCategories()
            // Never recreate the default test accounts on top of user data that merely failed to parse.
            if (users.isEmpty() && !usersDataCorrupt) seedUsers()
        }
        ensureCategoryCoverage()
        if (cloud == null) {
            // In cloud mode months and assignments come from Firestore, never from demo seeds.
            if (inventorySessions.isEmpty()) seedCurrentInventorySession()
            ensureInventoryAssignments()
        }
        ensureInventoryDepartments()
        if (currentInventorySessionId == null) {
            currentInventorySessionId = inventorySessions.firstOrNull { it.status == InventoryStatus.ACTIVE }?.id
                ?: inventorySessions.firstOrNull()?.id
        }
        persistAll()
    }

    // ---------------------------------------------------------------- storage

    private fun load() {
        fun key(key: String, parse: (String) -> Unit): Boolean =
            storage.readCollection(key, nowMillis(), parse) { backupKey ->
                loadWarnings += t("Lokale data («$key») kunne ikke leses. En kopi er tatt vare på ($backupKey).", "Local data («$key») could not be read. A copy has been kept ($backupKey).")
            }
        if (!key(StorageKeys.USERS) { replace(users, LocalCodec.parseUsers(it)) }) usersDataCorrupt = true
        key(StorageKeys.PRODUCTS) { replace(products, LocalCodec.parseProducts(it)) }
        key(StorageKeys.SESSIONS) { replace(sessions, LocalCodec.parseSessions(it)) }
        key(StorageKeys.INVENTORY_SESSIONS) { replace(inventorySessions, LocalCodec.parseInventorySessions(it)) }
        key(StorageKeys.CATEGORY_ASSIGNMENTS) { replace(categoryAssignments, LocalCodec.parseCategoryAssignments(it)) }
        key(StorageKeys.DEPARTMENTS) { replace(departments, LocalCodec.parseDepartments(it)) }
        key(StorageKeys.SELECTED_DEPARTMENTS) { selectedDepartmentBySession.clear(); selectedDepartmentBySession.putAll(LocalCodec.parseSelectedDepartments(it)) }
        key(StorageKeys.COUNT_ENTRIES) { replace(countEntries, LocalCodec.parseCountEntries(it)) }
        key(StorageKeys.CATEGORIES) { replace(categories, LocalCodec.parseCategories(it)) }
        key(StorageKeys.CATEGORY_ORDER) { replace(categoryOrder, LocalCodec.parseCategoryOrder(it)) }
        key(StorageKeys.PRODUCT_ORDERS) { productOrderByCategory.clear(); productOrderByCategory.putAll(LocalCodec.parseProductOrders(it)) }
        key(StorageKeys.SPOT_CHECKS) { replace(spotChecks, LocalCodec.parseSpotChecks(it)) }
        key(StorageKeys.AUDIT_EVENTS) { replace(auditEvents, LocalCodec.parseAuditEvents(it)) }
        key(StorageKeys.SVINN) { replace(svinn, LocalCodec.parseSvinn(it)) }
        key(StorageKeys.DELETED_PRODUCT_IDS) { deletedProductIds.clear(); deletedProductIds.addAll(LocalCodec.parseCategoryOrder(it)) }
        key(StorageKeys.DELETED_CATEGORY_NAMES) { deletedCategoryNames.clear(); deletedCategoryNames.addAll(LocalCodec.parseCategoryOrder(it)) }
        currentInventorySessionId = storage.getString(StorageKeys.CURRENT_INVENTORY_SESSION_ID)?.takeIf { it.isNotBlank() }
    }

    private fun <T> replace(target: MutableList<T>, items: List<T>) {
        target.clear()
        target += items
    }

    private fun persistAll() {
        storage.putStrings(
            mapOf(
                StorageKeys.USERS to LocalCodec.usersToJson(users),
                StorageKeys.PRODUCTS to LocalCodec.productsToJson(products),
                StorageKeys.SESSIONS to LocalCodec.sessionsToJson(sessions),
                StorageKeys.INVENTORY_SESSIONS to LocalCodec.inventorySessionsToJson(inventorySessions),
                StorageKeys.CATEGORY_ASSIGNMENTS to LocalCodec.categoryAssignmentsToJson(categoryAssignments),
                StorageKeys.DEPARTMENTS to LocalCodec.departmentsToJson(departments),
                StorageKeys.SELECTED_DEPARTMENTS to LocalCodec.selectedDepartmentsToJson(selectedDepartmentBySession),
                StorageKeys.COUNT_ENTRIES to LocalCodec.countEntriesToJson(countEntries),
                StorageKeys.CATEGORIES to LocalCodec.stringListToJson(categories),
                StorageKeys.CATEGORY_ORDER to LocalCodec.stringListToJson(categoryOrder),
                StorageKeys.PRODUCT_ORDERS to LocalCodec.productOrdersToJson(productOrderByCategory),
                StorageKeys.SPOT_CHECKS to LocalCodec.spotChecksToJson(spotChecks),
                StorageKeys.AUDIT_EVENTS to LocalCodec.auditEventsToJson(auditEvents),
                StorageKeys.SVINN to LocalCodec.svinnToJson(svinn),
                StorageKeys.CURRENT_INVENTORY_SESSION_ID to currentInventorySessionId,
                StorageKeys.DELETED_PRODUCT_IDS to LocalCodec.stringListToJson(deletedProductIds.toList()),
                StorageKeys.DELETED_CATEGORY_NAMES to LocalCodec.stringListToJson(deletedCategoryNames.toList())
            )
        )
        pushCloudChanges()
        changed()
    }

    /** Lightweight save for the hot counting path. */
    private fun persistCountState() {
        storage.putStrings(
            mapOf(
                StorageKeys.COUNT_ENTRIES to LocalCodec.countEntriesToJson(countEntries),
                StorageKeys.AUDIT_EVENTS to LocalCodec.auditEventsToJson(auditEvents)
            )
        )
        changed()
    }

    // ---------------------------------------------------------------- cloud

    /**
     * Receives local changes in cloud mode. Products, months (with departments), assignments and
     * memberships are found by comparing with what was last synchronized, so every screen action is
     * covered without its own cloud call; counts go as events with a base version (conflict check).
     */
    interface CloudSink {
        /** [previous] is the last synchronized version (null = new): only changed fields need to be sent. */
        fun upsertProduct(product: Product, previous: Product?)
        fun deleteProduct(productId: String)
        fun upsertSession(session: InventorySession, departments: List<InventoryDepartment>, previous: Pair<InventorySession, List<InventoryDepartment>>?)
        fun upsertAssignment(assignment: CategoryAssignment, previous: CategoryAssignment?)
        fun deleteAssignment(sessionId: String, category: String)
        fun upsertMembership(user: UserAccount, previous: UserAccount?)
        fun queueCountWrite(write: PendingCountWrite)
        /** Historikk: the closed month's snapshot (one document per month, replaced when closed again). */
        fun upsertHistory(snapshot: CountSession)
        /** Catalog: a hovedvare with its place in the order and its saved product order. */
        fun upsertCategory(documentId: String, doc: CategoryDoc)
        fun deleteCategory(documentId: String)
        /** Logg: one audit event (count edits stay local, see [CloudMapping.auditGoesToCloud]). */
        fun addAudit(event: AuditEvent)
        fun addSpotCheck(record: SpotCheckRecord)
        fun upsertSvinn(record: SvinnRecord)
        fun deleteSvinn(recordId: String)
        /** The shown month changed: listen to its assignments and counts. */
        fun sessionSelected(sessionId: String)
    }

    /** Set by the platform's cloud layer after sign-in; the current state counts as synchronized. */
    var cloudSink: CloudSink? = null
        set(value) {
            field = value
            resetCloudBaseline()
            currentInventorySessionId?.let { value?.sessionSelected(it) }
            lastSelectedForCloud = currentInventorySessionId
        }

    private val syncedProducts = mutableMapOf<String, Product>()
    private val syncedSessions = mutableMapOf<String, Pair<InventorySession, List<InventoryDepartment>>>()
    private val syncedAssignments = mutableMapOf<String, CategoryAssignment>()
    private val syncedMemberships = mutableMapOf<String, Map<String, Any?>>()
    private val syncedUsers = mutableMapOf<String, UserAccount>()
    /** Catalog documents as last seen in (or sent to) the cloud, by document id. */
    private val syncedCategoryDocs = mutableMapOf<String, CategoryDoc>()
    /** True once a complete catalog came from the server: before that nothing is pushed (it could be stale). */
    private var catalogFromServer = false
    /** Ids of snapshots, audit events and stikkprøver already in the cloud (or handed to it). Saved per company. */
    private val cloudUploadedIds = mutableSetOf<String>()
    private var cloudUploadedLoaded = false
    private val syncedSvinn = mutableMapOf<String, SvinnRecord>()
    /** The person signed in to the cloud (from the membership). */
    private var signedInUserId: String? = null
    private var lastSelectedForCloud: String? = null
    private var applyingRemote = false

    /** One key per Firestore document (the id is derived from the category, ignoring case). */
    private fun assignmentKey(a: CategoryAssignment) = assignmentKey(a.sessionId, a.category)
    private fun assignmentKey(sessionId: String, category: String) = "$sessionId|${CloudMapping.safeDocumentId(category)}"
    private fun sessionState(id: String): Pair<InventorySession, List<InventoryDepartment>>? =
        inventorySessions.firstOrNull { it.id == id }?.let { it to departmentsForSession(id) }
    /** Membership fields that are synchronized (no timestamps, no local-only data). */
    private fun membershipState(user: UserAccount): Map<String, Any?> =
        CloudMapping.membershipToCloud(user, cloud?.locationId.orEmpty(), cloud?.locationName.orEmpty(), 0L)
    private fun isCloudAccount(user: UserAccount) = user.password.isBlank() && user.username.contains('@')

    private fun resetCloudBaseline() {
        syncedProducts.clear(); products.forEach { syncedProducts[it.id] = it }
        syncedSessions.clear(); inventorySessions.forEach { s -> sessionState(s.id)?.let { syncedSessions[s.id] = it } }
        syncedAssignments.clear(); categoryAssignments.forEach { syncedAssignments[assignmentKey(it)] = it }
        syncedMemberships.clear(); syncedUsers.clear()
        users.filter(::isCloudAccount).forEach { syncedMemberships[it.id] = membershipState(it); syncedUsers[it.id] = it }
        syncedCategoryDocs.clear(); catalogFromServer = false
        syncedSvinn.clear(); svinn.forEach { syncedSvinn[it.id] = it }
        loadCloudUploadedIds()
    }

    /**
     * First time in the cloud with this version: every closed month (Historikk) on this device is
     * sent up, and the log and stikkprøver of the last 30 days (at most 200 / 100) – older entries
     * stay on the device so the free daily quota is not used up at once.
     */
    private fun loadCloudUploadedIds() {
        if (cloudUploadedLoaded) return
        cloudUploadedLoaded = true
        val raw = storage.getString(KEY_CLOUD_UPLOADED)
        if (raw != null) {
            cloudUploadedIds += runCatching { LocalCodec.parseCategoryOrder(raw) }.getOrDefault(emptyList())
            return
        }
        val since = nowMillis() - 30L * 24 * 60 * 60 * 1000
        val recentAudit = auditEvents.filter { CloudMapping.auditGoesToCloud(it) && it.timestamp >= since }.take(200).map { it.id }.toSet()
        cloudUploadedIds += auditEvents.filter { CloudMapping.auditGoesToCloud(it) && it.id !in recentAudit }.map { it.id }
        val recentSpots = spotChecks.filter { it.createdAt >= since }.take(100).map { it.id }.toSet()
        cloudUploadedIds += spotChecks.map { it.id }.filter { it !in recentSpots }
        saveCloudUploadedIds()
    }

    private fun saveCloudUploadedIds() {
        storage.putStrings(mapOf(KEY_CLOUD_UPLOADED to LocalCodec.stringListToJson(cloudUploadedIds.toList())))
    }

    /** Admins and people allowed to edit hovedvarer keep the cloud catalog (rules allow only them). */
    private fun mayEditCatalog(): Boolean = signedInMay(Permission.PRODUCT_EDIT_CATEGORY) || signedInMay(Permission.INVENTORY_ASSIGN_CATEGORY)

    /** Same check as the rules' hasPermission (admins may everything); leaders may close months. */
    private fun signedInMay(permission: Permission): Boolean {
        val me = users.firstOrNull { it.id == signedInUserId } ?: return false
        return me.role == Role.ADMIN || permission in me.permissions ||
            (permission == Permission.INVENTORY_CLOSE && me.role == Role.LEADER)
    }

    /** Hands everything that changed locally since the last sync to [cloudSink]. */
    private fun pushCloudChanges() {
        val sink = cloudSink ?: return
        if (applyingRemote) return
        products.forEach { p -> if (syncedProducts[p.id] != p) { sink.upsertProduct(p, syncedProducts[p.id]); syncedProducts[p.id] = p } }
        (syncedProducts.keys - products.map { it.id }.toSet()).forEach { id -> sink.deleteProduct(id); syncedProducts.remove(id) }
        inventorySessions.forEach { s ->
            val state = sessionState(s.id) ?: return@forEach
            if (syncedSessions[s.id] != state) { sink.upsertSession(state.first, state.second, syncedSessions[s.id]); syncedSessions[s.id] = state }
        }
        categoryAssignments.forEach { a ->
            val key = assignmentKey(a)
            if (syncedAssignments[key] != a) { sink.upsertAssignment(a, syncedAssignments[key]); syncedAssignments[key] = a }
        }
        val currentKeys = categoryAssignments.map(::assignmentKey).toSet()
        syncedAssignments.filterKeys { it !in currentKeys }.values.toList().forEach { a ->
            sink.deleteAssignment(a.sessionId, a.category)
            syncedAssignments.remove(assignmentKey(a))
        }
        users.filter(::isCloudAccount).forEach { u ->
            val state = membershipState(u)
            if (syncedMemberships[u.id] != state) { sink.upsertMembership(u, syncedUsers[u.id]); syncedMemberships[u.id] = state; syncedUsers[u.id] = u }
        }
        if (currentInventorySessionId != lastSelectedForCloud) {
            lastSelectedForCloud = currentInventorySessionId
            currentInventorySessionId?.let { sink.sessionSelected(it) }
        }
        pushCatalogAndRecords(sink)
    }

    private fun pushCatalogAndRecords(sink: CloudSink) {
        var uploaded = false
        // Each kind only when the rules allow it for the signed-in person; the rest stays on this device.
        val mayClose = signedInMay(Permission.INVENTORY_CLOSE)
        sessions.indices.forEach { i ->
            val snapshot = sessions[i]
            if (snapshot.id !in cloudUploadedIds && mayClose) {
                sink.upsertHistory(snapshot)
                cloudUploadedIds += snapshot.id; uploaded = true
                // "SENDER" until the cloud listener brings it back ("BEKREFTET").
                sessions[i] = snapshot.copy(syncState = CloudSaveState.UPLOADING)
            }
        }
        auditEvents.forEach { event ->
            if (event.id !in cloudUploadedIds && CloudMapping.auditGoesToCloud(event)) {
                if (event.actorUserId == signedInUserId) sink.addAudit(event)
                cloudUploadedIds += event.id; uploaded = true
            }
        }
        val maySpot = signedInMay(Permission.SPOTCHECK_PERFORM)
        spotChecks.forEach { record ->
            if (record.id !in cloudUploadedIds) {
                if (maySpot && !record.sessionId.isNullOrBlank()) sink.addSpotCheck(record)
                cloudUploadedIds += record.id; uploaded = true
            }
        }
        if (uploaded) saveCloudUploadedIds()
        // Svinn: own registrations by anyone; changes and deletions by admins (rules).
        val me = users.firstOrNull { it.id == signedInUserId }
        val isAdmin = me?.role == Role.ADMIN
        svinn.forEach { record ->
            if (syncedSvinn[record.id] != record && (isAdmin || (record.userId == signedInUserId && syncedSvinn[record.id] == null))) {
                sink.upsertSvinn(record); syncedSvinn[record.id] = record
            }
        }
        if (isAdmin) {
            val ids = svinn.map { it.id }.toSet()
            (syncedSvinn.keys - ids).forEach { id -> sink.deleteSvinn(id); syncedSvinn.remove(id) }
        }
        if (catalogFromServer && mayEditCatalog()) {
            val wanted = CloudMapping.catalogDocs(orderedCategoriesForCloud(), categoryOrder, productOrderByCategory)
            wanted.forEach { (id, doc) -> if (syncedCategoryDocs[id] != doc) { sink.upsertCategory(id, doc); syncedCategoryDocs[id] = doc } }
            (syncedCategoryDocs.keys - wanted.keys).forEach { id -> sink.deleteCategory(id); syncedCategoryDocs.remove(id) }
        }
    }

    private fun orderedCategoriesForCloud(): List<String> = categories.distinct()

    /** Applies data that came from Firestore without sending it back. */
    private inline fun fromCloud(block: () -> Unit) {
        applyingRemote = true
        try { block() } finally { applyingRemote = false }
    }

    /** The signed-in person's own account (from the membership), replacing any older copy. */
    fun applySignedInUser(account: UserAccount) = fromCloud {
        val withCategories = account.copy(
            allowedCategories = if (account.role == Role.ADMIN || Permission.INVENTORY_VIEW_OTHER in account.permissions) categories.toSet()
            else CloudMapping.categoriesAssignedTo(account.id, categoryAssignments)
        )
        val index = users.indexOfFirst { it.id == account.id }
        if (index >= 0) users[index] = withCategories else users += withCategories
        syncedMemberships[account.id] = membershipState(withCategories)
        syncedUsers[account.id] = withCategories
        signedInUserId = account.id
        persistAll()
    }

    fun applyCloudProducts(remote: List<Map<String, Any?>>) = fromCloud {
        if (remote.isEmpty()) return@fromCloud
        // Index once: a snapshot can hold 1000+ products and arrives after every confirmed write.
        val indexById = HashMap<String, Int>(products.size * 2)
        products.forEachIndexed { i, p -> indexById[p.id] = i }
        val categorySet = categories.toHashSet()
        val leftCategories = mutableSetOf<String>()
        var changed = false
        remote.forEach { map ->
            val product = CloudMapping.productFromCloud(map) ?: return@forEach
            if (product.id in deletedProductIds) {
                // Deleted here while offline: an admin deletes it in the cloud again. Anyone else
                // could never delete it (rules), so the product is accepted back instead of looping.
                if (users.firstOrNull { it.id == signedInUserId }?.role == Role.ADMIN) {
                    cloudSink?.deleteProduct(product.id)
                    return@forEach
                }
                deletedProductIds -= product.id
            }
            val index = indexById[product.id] ?: -1
            if (index >= 0) {
                val before = products[index]
                if (before != product) {
                    if (before.category != product.category) leftCategories += before.category
                    products[index] = product; changed = true
                }
            } else {
                indexById[product.id] = products.size
                products += product
                changed = true
            }
            if (product.category.isNotBlank() && categorySet.add(product.category)) { categories += product.category; changed = true }
            syncedProducts[product.id] = product
        }
        // Renamed or merged on another device: the old hovedvare is left without products – drop it here too.
        val emptied = leftCategories.filter { c -> products.none { it.category == c } && CloudMapping.categoryDocumentId(c) !in syncedCategoryDocs }
        if (emptied.isNotEmpty()) {
            categories.removeAll { it in emptied }
            categoryOrder.removeAll { it in emptied }
            emptied.forEach { productOrderByCategory.remove(it) }
        }
        if (changed) persistAll()
    }

    /** Historikk from the cloud: a month closed on another device (or closed again) replaces the local copy. */
    fun applyCloudHistory(remote: List<Map<String, Any?>>) {
        if (remote.isEmpty()) return
        fromCloud {
            var changed = false
            remote.forEach { map ->
                val snapshot = CloudMapping.historyFromCloud(map) ?: return@forEach
                val docId = CloudMapping.historyDocumentId(snapshot)
                cloudUploadedIds += snapshot.id
                val existing = sessions.firstOrNull { CloudMapping.historyDocumentId(it) == docId }
                if (existing?.id == snapshot.id && existing.lines.size == snapshot.lines.size) {
                    if (existing.syncState != CloudSaveState.CLOUD_CONFIRMED) {
                        sessions[sessions.indexOf(existing)] = existing.copy(syncState = CloudSaveState.CLOUD_CONFIRMED, serverAckAt = nowMillis())
                        changed = true
                    }
                    return@forEach
                }
                sessions.removeAll { CloudMapping.historyDocumentId(it) == docId }
                sessions += snapshot
                changed = true
            }
            if (changed) {
                sessions.sortByDescending { it.createdAt }
                saveCloudUploadedIds()
                persistAll()
            }
        }
    }

    /**
     * The cloud catalog: every hovedvare (also empty ones), the order of hovedvarer and the saved
     * product order per hovedvare. [complete] = the server's full list (not the offline cache).
     */
    fun applyCloudCategories(remote: List<Map<String, Any?>>, removedIds: List<String>, complete: Boolean) {
        fromCloud {
            removedIds.forEach { id ->
                val doc = syncedCategoryDocs.remove(id) ?: return@forEach
                // Deleted (or merged/renamed) elsewhere: gone here too once it has no products.
                if (products.none { it.category == doc.name }) {
                    categories.remove(doc.name); categoryOrder.remove(doc.name); productOrderByCategory.remove(doc.name)
                }
            }
            remote.forEach { map ->
                val doc = CloudMapping.categoryFromCloud(map) ?: return@forEach
                syncedCategoryDocs[CloudMapping.categoryDocumentId(doc.name)] = doc
                if (categories.none { it == doc.name }) {
                    categories.removeAll { it.equals(doc.name, ignoreCase = true) && products.none { p -> p.category == it } }
                    categories += doc.name
                }
                if (doc.productOrder.isNotEmpty()) productOrderByCategory[doc.name] = doc.productOrder else productOrderByCategory.remove(doc.name)
            }
            val ordered = syncedCategoryDocs.values.filter { it.sortIndex != null }.sortedBy { it.sortIndex }.map { it.name }
            if (ordered.isNotEmpty()) { categoryOrder.clear(); categoryOrder += ordered }
            if (complete && !catalogFromServer) {
                catalogFromServer = true
                if (syncedCategoryDocs.isNotEmpty()) {
                    // A hovedvare kept only here, without products and without a cloud document, was deleted elsewhere.
                    val known = syncedCategoryDocs.values.map { it.name }.toSet()
                    val stale = categories.filter { c -> c !in known && products.none { it.category == c } }
                    categories.removeAll(stale); categoryOrder.removeAll(stale); stale.forEach { productOrderByCategory.remove(it) }
                }
            }
            persistAll()
        }
        // Hovedvarer that exist here but not yet in the cloud (first time with this version) are sent up.
        if (complete) pushCloudChanges()
    }

    /** Svinn from the cloud: new and changed registrations, and ones an admin deleted. */
    fun applyCloudSvinn(remote: List<Map<String, Any?>>, removedIds: List<String>) = fromCloud {
        var changed = false
        removedIds.forEach { id -> if (svinn.removeAll { it.id == id }) changed = true; syncedSvinn.remove(id) }
        remote.forEach { map ->
            val record = SvinnMapping.fromCloud(map) ?: return@forEach
            syncedSvinn[record.id] = record
            val index = svinn.indexOfFirst { it.id == record.id }
            if (index >= 0) { if (svinn[index] != record) { svinn[index] = record; changed = true } } else { svinn += record; changed = true }
        }
        if (changed) { svinn.sortByDescending { it.createdAt }; persistAll() }
    }

    /** Logg from the cloud (admins). */
    fun applyCloudAudit(remote: List<Map<String, Any?>>) {
        if (remote.isEmpty()) return
        fromCloud {
            val known = auditEvents.map { it.id }.toHashSet()
            val incoming = remote.mapNotNull { CloudMapping.auditFromCloud(it) }.filter { it.id !in known }
            if (incoming.isEmpty()) return@fromCloud
            incoming.forEach { cloudUploadedIds += it.id }
            auditEvents += incoming
            auditEvents.sortByDescending { it.timestamp }
            saveCloudUploadedIds()
            persistAll()
        }
    }

    fun applyCloudSpotChecks(remote: List<Map<String, Any?>>) {
        if (remote.isEmpty()) return
        fromCloud {
            val known = spotChecks.map { it.id }.toHashSet()
            val incoming = remote.mapNotNull { CloudMapping.spotCheckFromCloud(it) }.filter { it.id !in known }
            if (incoming.isEmpty()) return@fromCloud
            incoming.forEach { cloudUploadedIds += it.id }
            spotChecks += incoming
            spotChecks.sortByDescending { it.createdAt }
            saveCloudUploadedIds()
            persistAll()
        }
    }

    fun removeCloudProducts(ids: List<String>) = fromCloud {
        if (ids.isEmpty()) return@fromCloud
        products.removeAll { it.id in ids }
        ids.forEach { syncedProducts.remove(it) }
        // The deletion reached the cloud: a product created again later with the same id is welcome.
        deletedProductIds -= ids.toSet()
        productOrderByCategory.keys.toList().forEach { c -> productOrderByCategory[c] = productOrderByCategory[c].orEmpty().filterNot { it in ids } }
        persistAll()
    }

    /**
     * A complete product list from the server (not from the offline cache): products that are no
     * longer in the cloud – e.g. deleted in the Firebase console while this device was away – are
     * removed here too, and a hovedvare left without products disappears with them.
     */
    fun retainCloudProducts(remoteIds: Set<String>) {
        val gone = products.filter { it.id !in remoteIds }
        if (gone.isEmpty()) return
        val emptied = gone.map { it.category }.toSet()
        removeCloudProducts(gone.map { it.id })
        fromCloud {
            val empty = emptied.filter { c -> products.none { it.category == c } }
            if (empty.isNotEmpty()) {
                categories.removeAll { it in empty }
                categoryOrder.removeAll { it in empty }
                empty.forEach { productOrderByCategory.remove(it) }
                persistAll()
            }
        }
    }

    fun applyCloudMemberships(remote: List<Map<String, Any?>>) = fromCloud {
        remote.forEach { map ->
            val uid = map["id"]?.toString().orEmpty()
            if (uid.isBlank()) return@forEach
            val account = CloudMapping.membershipFromCloud(map, CloudMapping.categoriesAssignedTo(uid, categoryAssignments)) ?: return@forEach
            val index = users.indexOfFirst { it.id == uid }
            if (index >= 0) users[index] = account else users += account
            syncedMemberships[uid] = membershipState(account)
            syncedUsers[uid] = account
        }
        persistAll()
    }

    fun applyCloudSessions(remote: List<Map<String, Any?>>, now: Long = nowMillis()) = fromCloud {
        val ctx = cloud ?: return@fromCloud
        val incomingIds = mutableSetOf<String>()
        remote.forEach { map ->
            val parsed = CloudMapping.inventorySessionFromCloud(
                map, ctx.companyId, ctx.locationId, ctx.locationName, currentYearMonth(), now
            ) ?: return@forEach
            val incoming = parsed.session
            incomingIds += incoming.id
            if (parsed.departments.isNotEmpty()) {
                departments.removeAll { it.sessionId == incoming.id }
                departments.addAll(parsed.departments)
                val selected = selectedDepartmentBySession[incoming.id]
                if (selected == null || parsed.departments.none { it.id == selected }) {
                    selectedDepartmentBySession[incoming.id] = parsed.departments.minByOrNull { it.sortOrder }!!.id
                }
            } else if (departments.none { it.sessionId == incoming.id }) {
                addDefaultDepartments(incoming.id)
            }
            val index = inventorySessions.indexOfFirst { it.id == incoming.id }
            if (index >= 0) {
                if (CloudMapping.acceptIncomingSession(inventorySessions[index], incoming)) inventorySessions[index] = incoming
            } else inventorySessions.add(0, incoming)
            sessionState(incoming.id)?.let { syncedSessions[incoming.id] = it }
        }
        // A month archived on another device is not shown any more: move to the open one.
        val currentStatus = inventorySessions.firstOrNull { it.id == currentInventorySessionId }?.status
        if (currentStatus == InventoryStatus.ARCHIVED) {
            currentInventorySessionId = InventoryRules.selectionAfterArchive(inventorySessions) ?: currentInventorySessionId
        }
        if (currentInventorySessionId !in incomingIds) {
            currentInventorySessionId = inventorySessions.firstOrNull { it.id in incomingIds && it.status == InventoryStatus.ACTIVE }?.id
                ?: inventorySessions.firstOrNull { it.id in incomingIds && it.status == InventoryStatus.DRAFT }?.id
                ?: currentInventorySessionId
        }
        persistAll()
        // A newly chosen month needs its own listeners (outside fromCloud: nothing else is pushed).
        if (currentInventorySessionId != lastSelectedForCloud) {
            lastSelectedForCloud = currentInventorySessionId
            currentInventorySessionId?.let { cloudSink?.sessionSelected(it) }
        }
    }

    fun applyCloudAssignments(remote: List<Map<String, Any?>>, now: Long = nowMillis()) = fromCloud {
        remote.forEach { map ->
            val assignment = CloudMapping.assignmentFromCloud(map, now) ?: return@forEach
            val index = categoryAssignments.indexOfFirst { assignmentKey(it) == assignmentKey(assignment) }
            if (index >= 0) categoryAssignments[index] = assignment else categoryAssignments += assignment
            if (assignment.category.isNotBlank() && assignment.category !in categories) categories += assignment.category
            syncedAssignments[assignmentKey(assignment)] = assignment
        }
        refreshCloudUserCategories()
        persistAll()
    }

    fun removeCloudAssignments(sessionId: String, removedCategories: List<String>) = fromCloud {
        if (removedCategories.isEmpty()) return@fromCloud
        val removedKeys = removedCategories.map { assignmentKey(sessionId, it) }.toSet()
        categoryAssignments.removeAll { assignmentKey(it) in removedKeys }
        removedCategories.forEach { syncedAssignments.remove(assignmentKey(sessionId, it)) }
        refreshCloudUserCategories()
        persistAll()
    }

    private fun refreshCloudUserCategories() {
        users.indices.forEach { i ->
            val user = users[i]
            if (isCloudAccount(user) && user.role != Role.ADMIN && Permission.INVENTORY_VIEW_OTHER !in user.permissions) {
                users[i] = user.copy(allowedCategories = CloudMapping.categoriesAssignedTo(user.id, categoryAssignments))
            }
        }
    }

    fun applyCloudCountEntries(remote: List<RemoteCountEntry>) {
        remote.forEach { incoming ->
            val index = countEntries.indexOfFirst { it.sessionId == incoming.sessionId && it.productId == incoming.productId && it.departmentId == incoming.departmentId }
            val merged = CloudMapping.mergeRemoteCount(countEntries.getOrNull(index), incoming)
            if (merged != null) {
                if (index >= 0) countEntries[index] = merged else countEntries += merged
            }
        }
        persistCountState()
    }

    fun applyCountAcknowledged(write: PendingCountWrite, newVersion: Int, now: Long = nowMillis()) {
        val index = countEntries.indexOfFirst { it.sessionId == write.sessionId && it.productId == write.productId && it.departmentId == write.departmentId }
        if (index >= 0) {
            val updated = CloudMapping.applyAcknowledgement(countEntries[index], write, newVersion, now)
            if (updated != countEntries[index]) countEntries[index] = updated
        }
        persistCountState()
    }

    /** A queued count became a conflict or was refused: it is no longer waiting. */
    fun applyCountNotPending(write: PendingCountWrite, rejectedReason: String? = null) {
        val index = countEntries.indexOfFirst { it.sessionId == write.sessionId && it.productId == write.productId && it.departmentId == write.departmentId }
        if (index >= 0 && countEntries[index].lastEventId == write.eventId) {
            countEntries[index] = countEntries[index].copy(pendingCloud = false)
        }
        if (rejectedReason != null) {
            recordAudit(write.actorUserId, "CLOUD_REJECTED", "CountEntry", "${write.sessionId}:${write.departmentId}:${write.productId}",
                "quantity", afterValue = write.quantity?.let(::formatQuantity).orEmpty(),
                inventorySessionId = write.sessionId, productId = write.productId)
        }
        persistCountState()
    }

    /** "Bruk cloud" on a conflict: take the server's value locally. */
    fun applyConflictKeepCloud(sessionId: String, productId: String, departmentId: String, quantity: Double?, note: String, updatedBy: String, version: Int) {
        val index = countEntries.indexOfFirst { it.sessionId == sessionId && it.productId == productId && it.departmentId == departmentId }
        if (index < 0) return
        countEntries[index] = countEntries[index].copy(
            quantity = quantity, note = note, updatedBy = updatedBy, version = version,
            lastEventId = null, pendingCloud = false, updatedAt = nowMillis()
        )
        persistCountState()
    }

    private fun recordAudit(
        actorUserId: String,
        action: String,
        entityType: String,
        entityId: String,
        field: String = "",
        beforeValue: String = "",
        afterValue: String = "",
        inventorySessionId: String? = null,
        category: String? = null,
        productId: String? = null
    ) {
        val actor = users.firstOrNull { it.id == actorUserId }
        auditEvents.add(0, AuditEvent(
            actorUserId = actorUserId,
            actorName = actor?.name ?: "Ukjent bruker",
            action = action,
            entityType = entityType,
            entityId = entityId,
            field = field,
            beforeValue = beforeValue,
            afterValue = afterValue,
            source = auditSource,
            deviceId = deviceId,
            appVersion = appVersion,
            serverTimestamp = null,
            inventorySessionId = inventorySessionId,
            category = category,
            productId = productId
        ))
        while (auditEvents.size > MAX_AUDIT_EVENTS) auditEvents.removeAt(auditEvents.lastIndex)
    }

    // ---------------------------------------------------------------- accounts and login

    private fun seedUsers() {
        val all = categories.toSet()
        users += UserAccount(
            name = "Vareflow Admin", username = "admin", password = LocalPasswords.hash("admin123"),
            role = Role.ADMIN, allowedCategories = all
        )
        users += UserAccount(
            name = "Anna Demo", username = "anna", password = LocalPasswords.hash("1234"), role = Role.EMPLOYEE,
            allowedCategories = setOf("Kjøtt", "Drikke", "Mat").filter { it in categories }.toSet(),
            accessUntil = nowMillis() + 30 * 60_000L
        )
    }

    private fun migratePlaintextPasswords() {
        users.indices.forEach { index ->
            val user = users[index]
            if (user.password.isNotBlank() && !LocalPasswords.isHashed(user.password)) {
                users[index] = user.copy(password = LocalPasswords.hash(user.password))
            }
        }
    }

    /** Result of a local login attempt. Messages are the same as on the phone. */
    sealed interface LoginResult {
        data class Success(val user: UserAccount) : LoginResult
        data class Failure(val message: String) : LoginResult
    }

    fun login(username: String, password: String, now: Long = nowMillis()): LoginResult {
        val found = users.firstOrNull {
            it.password.isNotBlank() && it.username.equals(username.trim(), ignoreCase = true) && LocalPasswords.verify(password, it.password)
        }
        return when {
            found == null -> LoginResult.Failure(t("Feil brukernavn eller passord.", "Wrong username or password."))
            !found.active -> LoginResult.Failure(t("Brukeren er deaktivert. Kontakt en administrator.", "The user is deactivated. Contact an administrator."))
            found.role == Role.EMPLOYEE && found.accessUntil.let { it == null || it <= now } ->
                LoginResult.Failure(t("Tilgangen er stengt. Be en admin åpne varetellingen.", "Access is closed. Ask an admin to open the stock count."))
            else -> LoginResult.Success(found)
        }
    }

    fun rememberedUser(now: Long = nowMillis()): UserAccount? {
        if (storage.getString(KEY_REMEMBER) != "true") return null
        val id = storage.getString(KEY_REMEMBER_USER) ?: return null
        return users.firstOrNull { user ->
            user.id == id && user.active && (user.role != Role.EMPLOYEE || user.accessUntil?.let { it > now } == true)
        }
    }

    fun setRemembered(user: UserAccount?, remember: Boolean) {
        storage.putStrings(
            mapOf(
                KEY_REMEMBER to if (remember && user != null) "true" else null,
                KEY_REMEMBER_USER to if (remember && user != null) user.id else null
            )
        )
    }

    fun userById(id: String): UserAccount? = users.firstOrNull { it.id == id }

    fun addUser(user: UserAccount, actor: UserAccount? = null) {
        users += user.copy(password = LocalPasswords.ensureHashed(user.password))
        actor?.let { recordAudit(it.id, "CREATE", "UserMembership", user.id, afterValue = "${user.name} · ${user.role.name}") }
        if (user.role != Role.ADMIN) {
            categoryAssignments.indices.forEach { index ->
                val assignment = categoryAssignments[index]
                if (sessionIsOpen(assignment.sessionId) && assignment.category in user.allowedCategories) {
                    categoryAssignments[index] = assignment.copy(assigneeIds = assignment.assigneeIds + user.id, updatedAt = nowMillis())
                }
            }
        }
        persistAll()
    }

    fun updateUser(updatedInput: UserAccount, actor: UserAccount? = null) {
        val updated = updatedInput.copy(password = LocalPasswords.ensureHashed(updatedInput.password))
        val index = users.indexOfFirst { it.id == updated.id }
        if (index < 0) return
        val before = users[index]
        users[index] = updated
        actor?.let { admin ->
            if (before.role != updated.role) recordAudit(admin.id, "UPDATE", "UserMembership", updated.id, "role", before.role.name, updated.role.name)
            if (before.permissions != updated.permissions) recordAudit(admin.id, "UPDATE", "UserMembership", updated.id, "permissions", before.permissions.joinToString { it.name }, updated.permissions.joinToString { it.name })
            if (before.allowedCategories != updated.allowedCategories) recordAudit(admin.id, "UPDATE", "UserMembership", updated.id, "categories", before.allowedCategories.joinToString(), updated.allowedCategories.joinToString())
            if (before.accessUntil != updated.accessUntil) recordAudit(admin.id, "UPDATE", "UserMembership", updated.id, "accessUntil", before.accessUntil?.toString().orEmpty(), updated.accessUntil?.toString().orEmpty())
            if (before.active != updated.active) recordAudit(admin.id, "UPDATE", "UserMembership", updated.id, "active", before.active.toString(), updated.active.toString())
        }
        if (before.allowedCategories != updated.allowedCategories) {
            categoryAssignments.indices.forEach { i ->
                val assignment = categoryAssignments[i]
                if (!sessionIsOpen(assignment.sessionId)) return@forEach
                val shouldBeAssigned = assignment.category in updated.allowedCategories && updated.active
                val currentlyAssigned = updated.id in assignment.assigneeIds
                if (shouldBeAssigned != currentlyAssigned) {
                    categoryAssignments[i] = assignment.copy(
                        assigneeIds = if (shouldBeAssigned) assignment.assigneeIds + updated.id else assignment.assigneeIds - updated.id,
                        updatedAt = nowMillis()
                    )
                }
            }
        }
        persistAll()
    }

    fun deactivateUser(userId: String, actor: UserAccount? = null) {
        val index = users.indexOfFirst { it.id == userId && it.role != Role.ADMIN }
        if (index < 0) return
        users[index] = users[index].copy(active = false, accessUntil = null)
        actor?.let { recordAudit(it.id, "DEACTIVATE", "UserMembership", userId, "active", "true", "false") }
        categoryAssignments.indices.forEach { i ->
            val assignment = categoryAssignments[i]
            if (userId in assignment.assigneeIds) {
                categoryAssignments[i] = assignment.copy(assigneeIds = assignment.assigneeIds - userId, updatedAt = nowMillis())
            }
        }
        persistAll()
    }

    // ---------------------------------------------------------------- catalog

    private fun importSeedProductsIfNeeded(seedProductsJson: () -> String?) {
        if (storage.getString(KEY_SEED_IMPORTED) == "true") return
        val imported = parseSeedProducts(seedProductsJson()).filterNot { it.id in deletedProductIds || it.category in deletedCategoryNames }
        if (imported.isEmpty()) return
        val custom = products.filterNot { it.id.startsWith("SRC-") }
        products.clear()
        products.addAll(imported)
        products.addAll(custom)
        storage.putStrings(mapOf(KEY_SEED_IMPORTED to "true"))
    }

    private fun applyPersistedDeletions() {
        if (deletedProductIds.isNotEmpty()) {
            products.removeAll { it.id in deletedProductIds }
            productOrderByCategory.keys.toList().forEach { category ->
                productOrderByCategory[category] = productOrderByCategory[category].orEmpty().filterNot { it in deletedProductIds }
            }
        }
        if (deletedCategoryNames.isNotEmpty()) {
            products.removeAll { it.category in deletedCategoryNames }
            categories.removeAll { it in deletedCategoryNames }
            categoryOrder.removeAll { it in deletedCategoryNames }
            deletedCategoryNames.forEach { productOrderByCategory.remove(it) }
        }
    }

    private fun seedCategories() {
        val fromProducts = products.map { it.category }.filter { it.isNotBlank() }.distinct()
        categories += sortNorwegian(fromProducts.ifEmpty { listOf("Drikke", "Kjøtt", "Mat", "Renhold") })
    }

    private fun ensureCategoryCoverage() {
        products.map { it.category.trim() }.filter { it.isNotBlank() }.distinct().forEach { category ->
            if (categories.none { it.equals(category, ignoreCase = true) }) categories += category
        }
    }

    fun orderedCategories(): List<String> {
        val alphabetical = sortNorwegian(categories.toList())
        if (categoryOrder.isEmpty()) return alphabetical
        val ordered = categoryOrder.filter { saved -> categories.any { it == saved } }.toMutableList()
        alphabetical.filterNot { it in ordered }.forEach { ordered += it }
        return ordered
    }

    fun orderedProducts(category: String): List<Product> {
        val categoryProducts = products.filter { it.category == category }
        val alphabetical = defaultProductOrder(categoryProducts)
        val savedOrder = productOrderByCategory[category].orEmpty()
        if (savedOrder.isEmpty()) return alphabetical
        val byId = categoryProducts.associateBy { it.id }
        val ordered = savedOrder.mapNotNull { byId[it] }.toMutableList()
        alphabetical.filterNot { product -> ordered.any { it.id == product.id } }.forEach { ordered += it }
        return ordered
    }

    private fun addAssignmentsForNewCategory(name: String) {
        inventorySessions.filter { it.status == InventoryStatus.DRAFT || it.status == InventoryStatus.ACTIVE }.forEach { session ->
            categoryAssignments += CategoryAssignment(session.id, name, InventoryRules.defaultAssignees(name, users))
        }
    }

    fun addCategory(rawName: String, actor: UserAccount? = null): Boolean {
        val name = rawName.trim()
        if (name.isBlank() || categories.any { it.equals(name, ignoreCase = true) }) return false
        categories += name
        deletedCategoryNames.remove(name)
        actor?.let { recordAudit(it.id, "CREATE", "Category", name, afterValue = name, category = name) }
        if (categoryOrder.isNotEmpty()) categoryOrder += name
        addAssignmentsForNewCategory(name)
        persistAll()
        return true
    }

    fun renameCategory(oldName: String, rawNewName: String, actor: UserAccount? = null): Boolean {
        val newName = rawNewName.trim()
        if (newName.isBlank()) return false
        if (oldName == newName) return true
        if (categories.any { it != oldName && it.equals(newName, ignoreCase = true) }) return false
        val categoryIndex = categories.indexOf(oldName)
        if (categoryIndex < 0) return false
        categories[categoryIndex] = newName
        val orderIndex = categoryOrder.indexOf(oldName)
        if (orderIndex >= 0) categoryOrder[orderIndex] = newName
        productOrderByCategory.remove(oldName)?.let { productOrderByCategory[newName] = it }
        products.indices.forEach { i -> if (products[i].category == oldName) products[i] = products[i].copy(category = newName) }
        replaceInDepartments(mapOf(oldName to newName))
        users.indices.forEach { i ->
            val user = users[i]
            if (oldName in user.allowedCategories) users[i] = user.copy(allowedCategories = (user.allowedCategories - oldName) + newName)
        }
        categoryAssignments.indices.forEach { i ->
            val a = categoryAssignments[i]
            if (a.category == oldName) categoryAssignments[i] = a.copy(category = newName, updatedAt = nowMillis())
        }
        actor?.let { recordAudit(it.id, "UPDATE", "Category", oldName, "name", oldName, newName, category = newName) }
        persistAll()
        return true
    }

    /** Another hovedvare with the same name (ignoring case), if renaming [category] to [rawName] would collide. */
    fun categoryNameTakenBy(category: String?, rawName: String): String? {
        val name = rawName.trim()
        return categories.firstOrNull { it != category && it.equals(name, ignoreCase = true) }
    }

    /**
     * Slå sammen: every product of [sources] moves to [rawTarget] – an existing hovedvare (matched
     * ignoring case) or a new name. Order, access and work assignments follow (assignees of the same
     * month are combined). The source hovedvarer disappear. Undergrupper are kept as they are.
     */
    fun mergeCategories(sources: Collection<String>, rawTarget: String, actor: UserAccount? = null): String? {
        val wanted = rawTarget.trim()
        if (wanted.isBlank()) return null
        val target = categories.firstOrNull { it == wanted } ?: categories.firstOrNull { it.equals(wanted, ignoreCase = true) } ?: wanted
        val from = sources.filter { it != target && it in categories }.distinct()
        if (from.isEmpty()) return null
        if (target !in categories) {
            categories.add(categories.indexOf(from.first()).coerceAtLeast(0), target)
            val orderIndex = categoryOrder.indexOf(from.first())
            if (orderIndex >= 0) categoryOrder.add(orderIndex, target)
        }
        val moved = mutableListOf<String>()
        from.forEach { source ->
            products.indices.forEach { i -> if (products[i].category == source) { products[i] = products[i].copy(category = target); moved += products[i].id } }
            val savedSource = productOrderByCategory.remove(source)
            val savedTarget = productOrderByCategory[target]
            if (savedSource != null || savedTarget != null) productOrderByCategory[target] = (savedTarget.orEmpty() + savedSource.orEmpty()).distinct()
            users.indices.forEach { i ->
                val user = users[i]
                if (source in user.allowedCategories) users[i] = user.copy(allowedCategories = (user.allowedCategories - source) + target)
            }
            categoryAssignments.filter { it.category == source }.forEach { a ->
                val index = categoryAssignments.indexOfFirst { it.sessionId == a.sessionId && it.category == target }
                if (index >= 0) {
                    val t = categoryAssignments[index]
                    categoryAssignments[index] = t.copy(assigneeIds = t.assigneeIds + a.assigneeIds, updatedAt = nowMillis())
                    categoryAssignments.remove(a)
                } else {
                    categoryAssignments[categoryAssignments.indexOf(a)] = a.copy(category = target, updatedAt = nowMillis())
                }
            }
            categories.remove(source)
            categoryOrder.remove(source)
        }
        replaceInDepartments(from.associateWith { target })
        actor?.let {
            recordAudit(it.id, "MERGE", "Category", target, "name", beforeValue = from.joinToString(", "),
                afterValue = t("${moved.size} varer flyttet til $target", "${moved.size} products moved to $target"), category = target)
        }
        persistAll()
        return target
    }

    /** Keeps "Hva telles her?" lists right when hovedvarer are renamed (→ new name), merged or deleted (→ null). */
    private fun replaceInDepartments(changes: Map<String, String?>) {
        departments.indices.forEach { i ->
            val d = departments[i]
            if (d.categories.none { it in changes }) return@forEach
            val updated = d.categories.mapNotNull { c -> if (c in changes) changes[c] else c }.distinct()
            // If every chosen hovedvare is gone, the list is empty again = all hovedvarer.
            departments[i] = d.copy(categories = updated)
        }
    }

    fun deleteCategory(category: String, actor: UserAccount? = null): Boolean {
        if (category !in categories) return false
        deletedCategoryNames += category
        products.filter { it.category == category }.map { it.id }.forEach { deleteProductInternal(it, actor) }
        categoryAssignments.removeAll { it.category == category }
        replaceInDepartments(mapOf(category to null))
        categories.remove(category)
        categoryOrder.remove(category)
        productOrderByCategory.remove(category)
        users.indices.forEach { i ->
            val user = users[i]
            if (category in user.allowedCategories) users[i] = user.copy(allowedCategories = user.allowedCategories - category)
        }
        actor?.let { recordAudit(it.id, "DELETE", "Category", category, beforeValue = category, category = category) }
        persistAll()
        return true
    }

    fun moveCategory(category: String, delta: Int) {
        val current = orderedCategories().toMutableList()
        val from = current.indexOf(category)
        if (from < 0) return
        val to = (from + delta).coerceIn(0, current.lastIndex)
        if (from == to) return
        current.add(to, current.removeAt(from))
        categoryOrder.clear()
        categoryOrder.addAll(current)
        persistAll()
    }

    fun moveProduct(category: String, productId: String, delta: Int) {
        val current = orderedProducts(category).toMutableList()
        val from = current.indexOfFirst { it.id == productId }
        if (from < 0) return
        val to = (from + delta).coerceIn(0, current.lastIndex)
        if (from == to) return
        current.add(to, current.removeAt(from))
        productOrderByCategory[category] = current.map { it.id }
        persistAll()
    }

    fun addProduct(product: Product, actor: UserAccount? = null) {
        deletedProductIds.remove(product.id)
        deletedCategoryNames.removeAll { it.equals(product.category, ignoreCase = true) }
        if (categories.none { it.equals(product.category, ignoreCase = true) }) {
            categories += product.category
            if (categoryOrder.isNotEmpty()) categoryOrder += product.category
            addAssignmentsForNewCategory(product.category)
        }
        products += product
        actor?.let { recordAudit(it.id, "CREATE", "Product", product.id, afterValue = product.name, category = product.category, productId = product.id) }
        productOrderByCategory[product.category]?.let { saved -> productOrderByCategory[product.category] = saved + product.id }
        persistAll()
    }

    /**
     * Import from a spreadsheet (ProductImport): adds [newProducts] and replaces [updated] in one go,
     * saved once, with one audit line. New hovedvarer appear with their products.
     */
    fun importProducts(newProducts: List<Product>, updated: List<Product>, actor: UserAccount?, source: String) {
        if (newProducts.isEmpty() && updated.isEmpty()) return
        newProducts.forEach { product ->
            deletedProductIds.remove(product.id)
            deletedCategoryNames.removeAll { it.equals(product.category, ignoreCase = true) }
            if (categories.none { it.equals(product.category, ignoreCase = true) }) {
                categories += product.category
                if (categoryOrder.isNotEmpty()) categoryOrder += product.category
                addAssignmentsForNewCategory(product.category)
            }
            products += product
            productOrderByCategory[product.category]?.let { saved -> productOrderByCategory[product.category] = saved + product.id }
        }
        updated.forEach { u ->
            val index = products.indexOfFirst { it.id == u.id }
            if (index >= 0) products[index] = u
        }
        actor?.let {
            recordAudit(it.id, "IMPORT", "Product", source, afterValue = t("${newProducts.size} nye, ${updated.size} oppdatert", "${newProducts.size} new, ${updated.size} updated"))
        }
        persistAll()
    }

    fun updateProduct(updated: Product, actor: UserAccount? = null) {
        val index = products.indexOfFirst { it.id == updated.id }
        if (index < 0) return
        val before = products[index]
        products[index] = updated
        if (before.category != updated.category) {
            productOrderByCategory[before.category]?.let { saved -> productOrderByCategory[before.category] = saved.filterNot { it == updated.id } }
            productOrderByCategory[updated.category]?.let { saved -> if (updated.id !in saved) productOrderByCategory[updated.category] = saved + updated.id }
            if (categories.none { it.equals(updated.category, ignoreCase = true) }) {
                categories += updated.category
                if (categoryOrder.isNotEmpty()) categoryOrder += updated.category
                addAssignmentsForNewCategory(updated.category)
            }
        }
        actor?.let { admin ->
            fun log(field: String, old: Any?, new: Any?) {
                if (old != new) recordAudit(admin.id, "UPDATE", "Product", updated.id, field, auditText(old), auditText(new), category = updated.category, productId = updated.id)
            }
            log("varenavn", before.name, updated.name)
            log("varenummer", before.varenummer, updated.varenummer)
            log("kategori", before.category, updated.category)
            log("enhet", before.unit, updated.unit)
            log("mvaRate", before.vat, updated.vat)
            log("purchasePriceExVat", before.pricePerUnit, updated.pricePerUnit)
            log("leverandør", before.supplier, updated.supplier)
            log("aliases", before.aliases.joinToString(), updated.aliases.joinToString())
            log("active", before.active, updated.active)
            log("importStatus", before.importStatus, updated.importStatus)
            log("dataQuality", before.dataQuality, updated.dataQuality)
        }
        persistAll()
    }

    private fun deleteProductInternal(productId: String, actor: UserAccount?) {
        deletedProductIds += productId
        val removed = products.firstOrNull { it.id == productId }
        products.removeAll { it.id == productId }
        removed?.let { product ->
            productOrderByCategory[product.category]?.let { saved -> productOrderByCategory[product.category] = saved.filterNot { it == productId } }
            actor?.let { recordAudit(it.id, "DELETE", "Product", product.id, beforeValue = product.name, category = product.category, productId = product.id) }
        }
    }

    fun deleteProduct(productId: String, actor: UserAccount? = null) {
        deleteProductInternal(productId, actor)
        persistAll()
    }

    // ---------------------------------------------------------------- monthly sessions and departments

    private fun sessionIsOpen(sessionId: String): Boolean =
        inventorySessions.firstOrNull { it.id == sessionId }?.status.let { it == InventoryStatus.DRAFT || it == InventoryStatus.ACTIVE }

    private fun addDefaultDepartments(sessionId: String) {
        departments += InventoryDepartment(id = "bar", sessionId = sessionId, name = "Bar", sortOrder = 0)
        departments += InventoryDepartment(id = "kjokken", sessionId = sessionId, name = "Kjøkken", sortOrder = 1)
        selectedDepartmentBySession[sessionId] = "bar"
    }

    private fun seedCurrentInventorySession() {
        val session = InventorySession(
            locationId = DomainDefaults.DEMO_LOCATION_ID,
            locationName = DomainDefaults.DEMO_LOCATION_NAME,
            yearMonth = currentYearMonth(),
            status = InventoryStatus.ACTIVE
        )
        inventorySessions += session
        addDefaultDepartments(session.id)
        currentInventorySessionId = session.id
    }

    private fun ensureInventoryAssignments() {
        inventorySessions.filter { it.status == InventoryStatus.DRAFT || it.status == InventoryStatus.ACTIVE }.forEach { session ->
            categories.forEach { category ->
                if (categoryAssignments.none { it.sessionId == session.id && it.category == category }) {
                    categoryAssignments += CategoryAssignment(session.id, category, InventoryRules.defaultAssignees(category, users))
                }
            }
        }
    }

    private fun ensureInventoryDepartments() {
        inventorySessions.forEach { session ->
            if (departments.none { it.sessionId == session.id }) addDefaultDepartments(session.id)
            val available = departments.filter { it.sessionId == session.id }.sortedBy { it.sortOrder }
            val selected = selectedDepartmentBySession[session.id]
            if (selected == null || available.none { it.id == selected }) {
                available.firstOrNull()?.let { selectedDepartmentBySession[session.id] = it.id }
            }
        }
    }

    fun currentInventorySession(): InventorySession? =
        InventoryRules.pickCurrentSession(currentInventorySessionId, inventorySessions)

    fun selectInventorySession(sessionId: String) {
        if (inventorySessions.any { it.id == sessionId }) {
            currentInventorySessionId = sessionId
            persistAll()
        }
    }

    fun createInventorySession(yearMonth: String, locationName: String, actor: UserAccount? = null): InventorySession? {
        val request = InventoryRules.validateNewSession(yearMonth, locationName, inventorySessions) ?: return null
        val hadSelectedSession = currentInventorySessionId != null
        val session = InventorySession(
            locationId = request.locationId, locationName = request.locationName,
            yearMonth = request.yearMonth, status = InventoryStatus.DRAFT
        )
        // The new month starts with the departments (and "Hva telles her?") of the latest month at the location.
        val previous = inventorySessions.filter { it.locationId == session.locationId && departments.any { d -> d.sessionId == it.id } }
            .maxWithOrNull(compareBy<InventorySession> { it.yearMonth }.thenBy { it.createdAt })
        inventorySessions.add(0, session)
        if (previous != null) {
            departmentsForSession(previous.id).forEach { d -> departments += d.copy(sessionId = session.id) }
            selectedDepartmentBySession[session.id] = departmentsForSession(session.id).first().id
        } else addDefaultDepartments(session.id)
        actor?.let { recordAudit(it.id, "CREATE", "InventorySession", session.id, afterValue = "${session.yearMonth} · ${session.locationName}", inventorySessionId = session.id) }
        categories.forEach { category -> categoryAssignments += CategoryAssignment(session.id, category, InventoryRules.defaultAssignees(category, users)) }
        if (!hadSelectedSession) currentInventorySessionId = session.id
        persistAll()
        return session
    }

    /** The month that stops a new count for [yearMonth] at [locationName] (also a closed one, which is hidden from the month tabs). */
    fun existingSessionFor(yearMonth: String, locationName: String): InventorySession? {
        val locationId = locationIdFromName(locationName.trim())
        return inventorySessions.firstOrNull { it.yearMonth == yearMonth.trim() && it.locationId == locationId && it.status != InventoryStatus.ARCHIVED }
    }

    /**
     * "Åpne igjen": a closed month is counted further. It becomes Aktiv again (Utkast if another
     * month at the location is already active). The newer snapshot version lets other devices
     * accept the reopening; closing it again replaces the earlier Historikk snapshot.
     */
    fun reopenInventorySession(sessionId: String, actor: UserAccount? = null): Boolean {
        val index = inventorySessions.indexOfFirst { it.id == sessionId }
        if (index < 0) return false
        val session = inventorySessions[index]
        if (session.status != InventoryStatus.CLOSED) return false
        val otherActive = inventorySessions.any { it.id != sessionId && it.locationId == session.locationId && it.status == InventoryStatus.ACTIVE }
        val status = if (otherActive) InventoryStatus.DRAFT else InventoryStatus.ACTIVE
        inventorySessions[index] = session.copy(status = status, closedAt = null, snapshotVersion = session.snapshotVersion + 1)
        currentInventorySessionId = sessionId
        actor?.let { recordAudit(it.id, "UPDATE", "InventorySession", sessionId, "status", session.status.name, status.name, inventorySessionId = sessionId) }
        persistAll()
        return true
    }

    fun activateInventorySession(sessionId: String, actor: UserAccount? = null): Boolean {
        val index = inventorySessions.indexOfFirst { it.id == sessionId }
        if (index < 0) return false
        val session = inventorySessions[index]
        if (!InventoryRules.canActivate(session, inventorySessions)) return false
        inventorySessions[index] = session.copy(status = InventoryStatus.ACTIVE)
        currentInventorySessionId = sessionId
        actor?.let { recordAudit(it.id, "UPDATE", "InventorySession", sessionId, "status", session.status.name, InventoryStatus.ACTIVE.name, inventorySessionId = sessionId) }
        persistAll()
        return true
    }

    fun archiveInventorySession(sessionId: String, actor: UserAccount? = null): Boolean {
        val index = inventorySessions.indexOfFirst { it.id == sessionId }
        if (index < 0) return false
        val session = inventorySessions[index]
        if (!InventoryRules.canArchive(session)) return false
        inventorySessions[index] = session.copy(status = InventoryStatus.ARCHIVED)
        actor?.let { recordAudit(it.id, "UPDATE", "InventorySession", sessionId, "status", session.status.name, InventoryStatus.ARCHIVED.name, inventorySessionId = sessionId) }
        if (currentInventorySessionId == sessionId) currentInventorySessionId = InventoryRules.selectionAfterArchive(inventorySessions)
        persistAll()
        return true
    }

    fun closeInventorySession(sessionId: String, closedBy: UserAccount): CountSession? {
        val index = inventorySessions.indexOfFirst { it.id == sessionId }
        if (index < 0) return null
        val session = inventorySessions[index]
        if (!InventoryRules.canClose(session, assignmentsForSession(sessionId))) return null
        val snapshot = CountSession(
            inventorySessionId = session.id, companyId = session.companyId, locationId = session.locationId,
            locationName = session.locationName, yearMonth = session.yearMonth, userId = closedBy.id,
            userName = closedBy.name, createdAt = nowMillis(),
            lines = InventoryRules.buildClosingLines(sessionId, countEntries, products, departments),
            syncState = CloudSaveState.LOCAL_SAVED
        )
        // Closed again after "Åpne igjen": the new snapshot replaces the earlier one in Historikk.
        sessions.removeAll { it.inventorySessionId == session.id }
        sessions.add(0, snapshot)
        inventorySessions[index] = session.copy(status = InventoryStatus.CLOSED, closedAt = nowMillis(), snapshotVersion = session.snapshotVersion + 1)
        recordAudit(closedBy.id, "UPDATE", "InventorySession", sessionId, "status", session.status.name, InventoryStatus.CLOSED.name, inventorySessionId = sessionId)
        persistAll()
        return snapshot
    }

    fun departmentsForSession(sessionId: String): List<InventoryDepartment> =
        departments.filter { it.sessionId == sessionId }.sortedWith(compareBy<InventoryDepartment> { it.sortOrder }.thenBy { it.name })

    fun selectedDepartmentId(sessionId: String): String {
        val available = departmentsForSession(sessionId)
        val selected = selectedDepartmentBySession[sessionId]
        if (selected != null && available.any { it.id == selected }) return selected
        return available.firstOrNull()?.id ?: "bar"
    }

    fun selectDepartment(sessionId: String, departmentId: String) {
        if (departments.any { it.sessionId == sessionId && it.id == departmentId }) {
            selectedDepartmentBySession[sessionId] = departmentId
            persistAll()
        }
    }

    fun addDepartment(sessionId: String, name: String, actor: UserAccount? = null): InventoryDepartment? {
        val clean = name.trim()
        if (clean.isBlank()) return null
        if (departments.any { it.sessionId == sessionId && it.name.equals(clean, ignoreCase = true) }) return null
        val nextOrder = (departments.filter { it.sessionId == sessionId }.maxOfOrNull { it.sortOrder } ?: -1) + 1
        val department = InventoryDepartment(sessionId = sessionId, name = clean, sortOrder = nextOrder)
        departments += department
        selectedDepartmentBySession[sessionId] = department.id
        actor?.let { recordAudit(it.id, "CREATE", "Department", department.id, afterValue = clean, inventorySessionId = sessionId) }
        persistAll()
        return department
    }

    /**
     * "Hva telles her?": the hovedvarer counted in a department (empty = all). Only admins (screens).
     * Hovedvarer that no longer exist are dropped from the list.
     */
    fun setDepartmentCategories(sessionId: String, departmentId: String, chosen: List<String>, actor: UserAccount? = null): Boolean {
        val index = departments.indexOfFirst { it.sessionId == sessionId && it.id == departmentId }
        if (index < 0) return false
        val before = departments[index]
        val clean = orderedCategories().filter { it in chosen }
        if (before.categories == clean) return true
        departments[index] = before.copy(categories = clean)
        actor?.let {
            recordAudit(it.id, "UPDATE", "Department", departmentId, "categories",
                before.categories.ifEmpty { listOf(t("Alle", "All")) }.joinToString(", "),
                clean.ifEmpty { listOf(t("Alle", "All")) }.joinToString(", "), inventorySessionId = sessionId)
        }
        persistAll()
        return true
    }

    /** The hovedvarer shown in Telling for a department, in the usual order. */
    fun categoriesForDepartment(sessionId: String, departmentId: String?, visible: List<String>): List<String> {
        val department = departments.firstOrNull { it.sessionId == sessionId && it.id == departmentId } ?: return visible
        return visible.filter { department.counts(it) }
    }

    // ---------------------------------------------------------------- svinn

    /** Registers svinn for [product] in the shown month (anyone who counts may register). */
    fun addSvinn(product: Product, quantity: Double, reason: SvinnReason, note: String, user: UserAccount): SvinnRecord? {
        if (quantity <= 0.0 || quantity.isNaN()) return null
        val session = currentInventorySession()
        val record = SvinnRecord(
            yearMonth = session?.yearMonth ?: currentYearMonth(), sessionId = session?.id,
            productId = product.id, productName = product.name, category = product.category, unit = product.unit,
            pricePerUnit = product.pricePerUnit, quantity = quantity, reason = reason, note = note.trim(),
            userId = user.id, userName = user.name
        )
        svinn.add(0, record)
        recordAudit(user.id, "CREATE", "Svinn", record.id, "quantity", "", "${formatQuantity(quantity)} ${product.unit} · ${reason.label}",
            inventorySessionId = session?.id, category = product.category, productId = product.id)
        persistAll()
        return record
    }

    fun updateSvinn(id: String, quantity: Double, reason: SvinnReason, note: String, actor: UserAccount): Boolean {
        if (quantity <= 0.0 || quantity.isNaN()) return false
        val index = svinn.indexOfFirst { it.id == id }
        if (index < 0) return false
        val before = svinn[index]
        svinn[index] = before.copy(quantity = quantity, reason = reason, note = note.trim(), updatedAt = nowMillis())
        recordAudit(actor.id, "UPDATE", "Svinn", id, "quantity", "${formatQuantity(before.quantity)} · ${before.reason.label}",
            "${formatQuantity(quantity)} · ${reason.label}", category = before.category, productId = before.productId)
        persistAll()
        return true
    }

    fun deleteSvinn(id: String, actor: UserAccount): Boolean {
        val record = svinn.firstOrNull { it.id == id } ?: return false
        svinn.remove(record)
        recordAudit(actor.id, "DELETE", "Svinn", id, beforeValue = "${record.productName} ${formatQuantity(record.quantity)} ${record.unit}",
            category = record.category, productId = record.productId)
        persistAll()
        return true
    }

    fun svinnFor(yearMonth: String): List<SvinnRecord> = svinn.filter { it.yearMonth == yearMonth }

    fun renameDepartment(sessionId: String, departmentId: String, name: String, actor: UserAccount? = null): Boolean {
        val clean = name.trim()
        if (clean.isBlank()) return false
        if (departments.any { it.sessionId == sessionId && it.id != departmentId && it.name.equals(clean, ignoreCase = true) }) return false
        val index = departments.indexOfFirst { it.sessionId == sessionId && it.id == departmentId }
        if (index < 0) return false
        val before = departments[index]
        departments[index] = before.copy(name = clean)
        actor?.let { recordAudit(it.id, "UPDATE", "Department", departmentId, "name", before.name, clean, inventorySessionId = sessionId) }
        persistAll()
        return true
    }

    fun deleteDepartment(sessionId: String, departmentId: String, actor: UserAccount? = null): Boolean {
        val available = departmentsForSession(sessionId)
        if (available.size <= 1) return false
        val department = available.firstOrNull { it.id == departmentId } ?: return false
        departments.removeAll { it.sessionId == sessionId && it.id == departmentId }
        countEntries.removeAll { it.sessionId == sessionId && it.departmentId == departmentId }
        if (selectedDepartmentBySession[sessionId] == departmentId) {
            selectedDepartmentBySession[sessionId] = departmentsForSession(sessionId).firstOrNull()?.id ?: "bar"
        }
        actor?.let { recordAudit(it.id, "DELETE", "Department", departmentId, beforeValue = department.name, inventorySessionId = sessionId) }
        persistAll()
        return true
    }

    // ---------------------------------------------------------------- assignments and counting

    fun assignmentsForSession(sessionId: String): List<CategoryAssignment> = categoryAssignments.filter { it.sessionId == sessionId }

    fun assignment(sessionId: String, category: String): CategoryAssignment? =
        categoryAssignments.firstOrNull { it.sessionId == sessionId && it.category == category }

    fun setCategoryAssignees(sessionId: String, category: String, assigneeIds: Set<String>, actor: UserAccount? = null) {
        val index = categoryAssignments.indexOfFirst { it.sessionId == sessionId && it.category == category }
        val existing = if (index >= 0) categoryAssignments[index] else CategoryAssignment(sessionId, category)
        val updated = existing.copy(assigneeIds = assigneeIds, updatedAt = nowMillis())
        if (index >= 0) categoryAssignments[index] = updated else categoryAssignments += updated
        if (existing.assigneeIds != assigneeIds) actor?.let {
            val names = { ids: Set<String> -> ids.mapNotNull { id -> users.firstOrNull { u -> u.id == id }?.name }.joinToString() }
            recordAudit(it.id, "UPDATE", "CategoryAssignment", "$sessionId:$category", "assignees", names(existing.assigneeIds), names(assigneeIds), inventorySessionId = sessionId, category = category)
        }
        persistAll()
    }

    fun setCategoryStatus(sessionId: String, category: String, status: CategoryWorkStatus, actor: UserAccount? = null) {
        val index = categoryAssignments.indexOfFirst { it.sessionId == sessionId && it.category == category }
        val existing = if (index >= 0) categoryAssignments[index] else CategoryAssignment(sessionId, category)
        val updated = existing.copy(status = status, updatedAt = nowMillis())
        if (index >= 0) categoryAssignments[index] = updated else categoryAssignments += updated
        if (existing.status != status) actor?.let { recordAudit(it.id, "UPDATE", "CategoryAssignment", "$sessionId:$category", "status", existing.status.name, status.name, inventorySessionId = sessionId, category = category) }
        persistAll()
    }

    fun visibleCategories(sessionId: String, user: UserAccount): List<String> =
        AccessRules.visibleCategories(orderedCategories(), assignmentsForSession(sessionId), user)

    fun canEditCategory(sessionId: String, category: String, user: UserAccount): Boolean =
        AccessRules.canEditCategory(inventorySessions.firstOrNull { it.id == sessionId }, assignment(sessionId, category), user)

    fun canReopenCategory(sessionId: String, category: String, user: UserAccount): Boolean =
        AccessRules.canReopenCategory(inventorySessions.firstOrNull { it.id == sessionId }, assignment(sessionId, category), user)

    fun entry(sessionId: String, productId: String, departmentId: String): InventoryCountEntry? =
        countEntries.firstOrNull { it.sessionId == sessionId && it.productId == productId && it.departmentId == departmentId }

    fun saveCountEntry(sessionId: String, productId: String, departmentId: String, quantity: Double?, note: String, updatedBy: String) {
        val session = inventorySessions.firstOrNull { it.id == sessionId } ?: return
        if (!InventoryRules.countsAreEditable(session)) return
        val index = countEntries.indexOfFirst { it.sessionId == sessionId && it.productId == productId && it.departmentId == departmentId }
        val previous = if (index >= 0) countEntries[index] else null
        val sink = cloudSink
        val ctx = cloud
        // Cloud: every change is an event (kept even when cleared, so the clearing reaches the others).
        val eventId = if (sink != null && ctx != null) randomId() else null
        val now = nowMillis()
        if (quantity == null && note.isBlank() && eventId == null) {
            if (index >= 0) countEntries.removeAt(index)
        } else {
            val entry = InventoryCountEntry(
                sessionId = sessionId, productId = productId, departmentId = departmentId, quantity = quantity,
                note = note, updatedBy = updatedBy, updatedAt = now,
                version = previous?.version ?: 0, lastEventId = eventId ?: previous?.lastEventId, pendingCloud = eventId != null
            )
            if (index >= 0) countEntries[index] = entry else countEntries += entry
        }
        if (sink != null && ctx != null && eventId != null) {
            sink.queueCountWrite(
                PendingCountWrite(
                    eventId = eventId, companyId = ctx.companyId, locationId = ctx.locationId, sessionId = sessionId,
                    productId = productId, departmentId = departmentId, quantity = quantity, note = note,
                    actorUserId = updatedBy, actorName = users.firstOrNull { it.id == updatedBy }?.name.orEmpty(),
                    baseVersion = previous?.version ?: 0, createdAt = now
                )
            )
        }
        val category = products.firstOrNull { it.id == productId }?.category
        if (previous?.quantity != quantity) {
            recordAudit(updatedBy, "UPDATE", "CountEntry", "$sessionId:$departmentId:$productId", "quantity",
                previous?.quantity?.let(::formatQuantity).orEmpty(), quantity?.let(::formatQuantity).orEmpty(),
                inventorySessionId = sessionId, category = category, productId = productId)
        }
        if (previous?.note.orEmpty() != note) {
            recordAudit(updatedBy, "UPDATE", "CountEntry", "$sessionId:$departmentId:$productId", "note",
                previous?.note.orEmpty(), note, inventorySessionId = sessionId, category = category, productId = productId)
        }
        val assignment = category?.let { assignment(sessionId, it) }
        if (category != null && assignment != null && assignment.status == CategoryWorkStatus.NOT_STARTED && (quantity != null || note.isNotBlank())) {
            setCategoryStatus(sessionId, category, CategoryWorkStatus.IN_PROGRESS, users.firstOrNull { it.id == updatedBy })
        } else {
            persistCountState()
        }
    }

    fun countedCount(sessionId: String, category: String? = null): Int =
        InventoryRules.countedCount(sessionId, countEntries, products, category)

    fun totalToCount(sessionId: String, category: String? = null): Int =
        InventoryRules.totalToCount(products, category, departmentsForSession(sessionId).size)

    /** Counted products of [category] in one department (for the x/y on the category strip). */
    fun countedInDepartment(sessionId: String, departmentId: String, category: String): Int {
        val ids = products.filter { it.active && it.category == category }.map { it.id }.toSet()
        return countEntries.count { it.sessionId == sessionId && it.departmentId == departmentId && it.productId in ids && it.quantity != null }
    }

    fun activeProductCount(category: String): Int = products.count { it.active && it.category == category }

    fun addSpotCheck(record: SpotCheckRecord) {
        spotChecks.add(0, record)
        recordAudit(record.checkerUserId, "SPOTCHECK", "SpotCheck", record.id, "result", formatQuantity(record.expectedQty),
            "${formatQuantity(record.checkedQty)} · ${if (record.matches) "TREFF" else "AVVIK"}",
            inventorySessionId = record.sessionId, productId = record.productId)
        persistAll()
    }

    companion object {
        const val MAX_AUDIT_EVENTS = 3000
        private const val KEY_DEVICE_ID = "device_id"
        private const val KEY_CLOUD_UPLOADED = "cloud_uploaded_ids"
        private const val KEY_REMEMBER = "remember_login"
        private const val KEY_REMEMBER_USER = "remember_user_id"
        private const val KEY_SEED_IMPORTED = "excel_148_imported_v1"

        /** Audit text for a changed value; numbers are written as on the phone (25.0, not 25). */
        fun auditText(value: Any?): String = when (value) {
            null -> ""
            is Double -> if (value == kotlin.math.floor(value) && !value.isInfinite() && kotlin.math.abs(value) < 1e15) "${value.toLong()}.0" else value.toString()
            else -> value.toString()
        }

        /** Parses the bundled product list (products_148.json). */
        fun parseSeedProducts(raw: String?): List<Product> {
            if (raw.isNullOrBlank()) return emptyList()
            return runCatching {
                val array = Json.parseArray(raw)
                List(array.length()) { i ->
                    val o = array.getObject(i)
                    Product(
                        id = o.getString("id"), varenummer = o.optString("varenummer"), name = o.getString("name"),
                        unit = o.getString("unit"), pricePerUnit = o.optDouble("pricePerUnit", 0.0),
                        category = o.getString("category"), vat = normalizeProductVat(o.optDouble("vat", 15.0)),
                        supplier = o.optString("supplier"), importStatus = o.optString("importStatus", "Klar"),
                        dataQuality = o.optString("dataQuality"), sourceRow = if (o.isNull("sourceRow")) null else o.optInt("sourceRow"),
                        sourceCategory = o.optString("sourceCategory"), active = o.optBoolean("active", true)
                    )
                }
            }.getOrDefault(emptyList())
        }
    }
}

/** The Firebase company and location a cloud-mode [VareflowCore] mirrors. */
data class CloudContext(val companyId: String, val locationId: String, val locationName: String)
